/**
 * WishMarker — Read-only color-coded marker for wish list items in the viewer.
 *
 * Unlike ItemMarker (which is editable), WishMarker shows:
 *  - A colored circle at the item position
 *  - A colored flag with heart icon + item name
 *  - On tap: detail card showing thumbnail, name, price, note, sender
 *
 * Color is assigned per-person from the WISH_COLORS palette.
 */
import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { WishItem } from '@/stores/wishlistStore'
import './WishMarker.css'

interface WishMarkerProps {
  item: WishItem
  /** Assigned color from WISH_COLORS palette */
  color: string
  /** Sender name (the person who made the wish) */
  senderName: string
  /** Display X (frame-relative pixels) */
  displayX: number
  /** Display Y (frame-relative pixels) */
  displayY: number
  /** Display radius (scaled to current frame size) */
  displayRadius: number
  /** Item number label */
  index: number
  /** When true, hide flag labels — only show circles (dots-only mode) */
  labelsHidden?: boolean
}

export function WishMarker({
  item,
  color,
  senderName,
  displayX,
  displayY,
  displayRadius,
  index,
  labelsHidden = false,
}: WishMarkerProps) {
  const { t } = useTranslation('delivery')
  const [expanded, setExpanded] = useState(false)
  const [peeking, setPeeking] = useState(false)

  const handleFlagClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setExpanded((prev) => !prev)
  }, [])

  const flagY = displayY - displayRadius

  return (
    <>
      {/* Colored circle overlay */}
      <div
        className={`wish-marker-circle ${labelsHidden ? 'wish-marker-circle-active' : ''}`}
        style={{
          left: `${displayX}px`,
          top: `${displayY}px`,
          width: `${displayRadius * 2}px`,
          height: `${displayRadius * 2}px`,
          borderColor: color,
          backgroundColor: `${color}10`,
          '--wish-color': color,
        } as React.CSSProperties}
        onClick={labelsHidden ? (e) => { e.stopPropagation(); setPeeking((p) => !p) } : undefined}
      />

      {/* Colored flag */}
      <div
        className={`wish-marker-flag ${labelsHidden && !peeking ? 'wish-marker-flag-hidden' : ''}`}
        style={{ left: `${displayX}px`, top: `${flagY}px` }}
        onClick={handleFlagClick}
      >
        <div className="wish-marker-tag" style={{ background: color, boxShadow: `0 2px 8px ${color}80` }}>
          {/* Heart icon */}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
          <span>{item.name || t('wishlist.itemFallback', { number: index })}</span>
        </div>
        <div className="wish-marker-stem" style={{ background: `${color}99` }} />

        {/* Expanded detail card */}
        {expanded && (
          <div className="wish-marker-detail" onClick={(e) => e.stopPropagation()}>
            {item.thumbnailUrl && (
              <img src={item.thumbnailUrl} alt="" className="wish-marker-detail-thumb" />
            )}
            <div className="wish-marker-detail-sender" style={{ color }}>
              💝 {senderName || t('wishlist.unknownSender')}
            </div>
            <div className="wish-marker-detail-name">
              {item.name || t('wishlist.itemFallback', { number: index })}
            </div>
            {item.price !== null && (
              <div className="wish-marker-detail-price">${item.price.toFixed(2)}</div>
            )}
            {item.note && (
              <div className="wish-marker-detail-note">📝 {item.note}</div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
